import { Edit3, Equal, MessageCircle, Trash2, X } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Pressable } from "react-native";
import { XStack, YStack } from "tamagui";
import { getDayJsFromDate } from "../../../core/dayJs";
import { useStores } from "../../../core/stores";
import { Body } from "../../../ui/Body";
import { Label } from "../../../ui/Label";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { Title } from "../../../ui/Title";
import { AvatarSize, UserAvatar } from "../../../ui/UserAvatar";
import type { TrackerHistoryItem } from "../data/TrackerUserData";
import type TrackerModuleModel from "../TrackerModuleModel";
import { AddEntryModal } from "./AddEntryModal";

export const HistoryItemRow: React.FC<{
  entry: TrackerHistoryItem;
  showEdit: boolean;
  module: TrackerModuleModel;
}> = observer(({ entry, showEdit, module }) => {
  const { usersStore } = useStores();

  const user = usersStore.byId.get(entry.userUid);

  const [showForm, setShowForm] = useState(false);

  const myUserData = module.myUserData;

  const option = module.getOption(entry.type);

  if ((showEdit && !myUserData) || !option || !user) {
    return null;
  }

  let calculation = null;

  const totalAmount = option.isDynamic
    ? entry.amount * option.weight
    : 1 * option.weight;

  if (option.isDynamic) {
    calculation = (
      <>
        <X size={"$small"} />
        <Body>{entry.amount}</Body>
      </>
    );
  }

  return (
    <>
      <XStack
        gap={"$medium"}
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <YStack gap="$small" flex={1}>
          <XStack gap="$medium" alignItems="flex-start">
            <UserAvatar userUid={user.uid} size={AvatarSize.Medium} />
            <YStack gap="$small" flex={1}>
              <XStack gap="$small" alignItems="center">
                <Label>{option.label}</Label>
                {calculation}
              </XStack>
              <Body numberOfLines={1} ellipsizeMode="tail" color={"$muted"}>
                {user.data.displayName}
              </Body>

              {!!entry.comment && (
                <XStack gap="$small" alignItems="center">
                  <MessageCircle size={"$small"} color="$muted" />
                  <Body fontStyle="italic">"{entry.comment}"</Body>
                </XStack>
              )}
              <Body muted>{getDayJsFromDate(new Date(entry.ts))}</Body>
            </YStack>
          </XStack>
        </YStack>

        <YStack gap="$medium">
          {showEdit && (
            <XStack gap="$medium" alignItems="center">
              <Pressable onPress={() => setShowForm(true)}>
                <Edit3 color={"$muted"} />
              </Pressable>
              <PopoverConfirm
                onConfirm={() => {
                  myUserData?.deleteEntry(entry);
                }}
              >
                <Trash2 size={"$size.normal"} color={"$muted"} />
              </PopoverConfirm>
            </XStack>
          )}

          <XStack gap="$small" alignItems="center">
            <Equal size={"$small"} />
            <Title>{Math.round(totalAmount)}</Title>
          </XStack>
        </YStack>
      </XStack>

      <AddEntryModal
        visible={showForm}
        onClose={() => setShowForm(false)}
        entry={entry}
        module={module}
      />
    </>
  );
});
